<template>
    <div :id="mapContainerId" style="height: 400px;"></div>
    <link href='https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.css' rel='stylesheet' />
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

export default {
    name: 'MapboxMap',
    props: {
        selectedParameter: {
            type: String,
            required: true
        },
        selectedIDs: {
            type: Array,
            required: true
        },
        initialBBOX: {
            type: Array,
            required: true
        },
        currentBbox: {
            type: Array,
            required: false,
            default: () => []
        },
        redrawBbox: {
            type: Boolean,
            default: false
        }
    },
    emits: ["update-bbox"],

    setup(props, { emit }) {
        const map = ref(null);
        let draw = null;
        
        // Generate unique ID for each map instance
        const mapContainerId = computed(() => `map-${Math.random().toString(36).substr(2, 9)}`);

        const onDrawCreated = (event) => {
            const { features } = event;
            if (features.length > 0) {
                const feature = features[0];
                const bbox = feature.geometry.coordinates[0].reduce((acc, coord) => {
                    acc.push(coord[0], coord[1]);
                    return acc;
                }, []);

                const longs = bbox.filter((_, i) => i % 2 === 0);
                const lats = bbox.filter((_, i) => i % 2 === 1);

                const minLong = Math.min(...longs);
                const minLat = Math.min(...lats);
                const maxLong = Math.max(...longs);
                const maxLat = Math.max(...lats);

                const simplifiedBbox = [minLong, minLat, maxLong, maxLat];
                emit('update-bbox', simplifiedBbox);

                // Remove the drawn polygon
                draw.deleteAll();
                return simplifiedBbox;
            }
        };
        const updateBboxLayer = (bbox) => {
            if (!map.value || !bbox || bbox.length !== 4) return;

            const geojson = {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [[
                        [bbox[0], bbox[1]],
                        [bbox[0], bbox[3]],
                        [bbox[2], bbox[3]],
                        [bbox[2], bbox[1]],
                        [bbox[0], bbox[1]]
                    ]]
                }
            };

            if (map.value.getSource('current-bbox')) {
                map.value.getSource('current-bbox').setData(geojson);
            } else {
                map.value.addLayer({
                    id: 'current-bbox',
                    type: 'fill',
                    source: {
                        type: 'geojson',
                        data: geojson
                    },
                    paint: {
                        'fill-color': '#088',
                        'fill-opacity': 0.3,
                        'fill-outline-color': '#088'
                    }
                });
            }
        };

        onMounted(() => {
            mapboxgl.accessToken = 'pk.eyJ1Ijoic2ltZW9udyIsImEiOiJjbTE3dThoMXowdDJ6Mm5zY3l6YnRwYnJqIn0.J9NB98kNT_vmotP-Lq4MQA';

            map.value = new mapboxgl.Map({
                container: mapContainerId.value,  // Use unique ID
                style: 'mapbox://styles/mapbox/streets-v11',
                center: [12.44, 51.08],
                zoom: 6,
                maxBounds: [
                    [props.initialBBOX[0], props.initialBBOX[1]],
                    [props.initialBBOX[2], props.initialBBOX[3]]
                ]
            });


            draw = new MapboxDraw({
                displayControlsDefault: false,
                controls: {
                    polygon: true,
                    line_string: false,
                    point: false,
                    trash: false
                }
            });

            map.value.addControl(draw);

            map.value.on('draw.create', onDrawCreated);

            map.value.on('load', () => {
                updateBboxLayer(props.currentBbox);
            });




            // Fit initial bounding box
            if (props.initialBBOX.length === 4) {
                const bounds = [
                    [props.initialBBOX[0], props.initialBBOX[1]],
                    [props.initialBBOX[2], props.initialBBOX[3]]
                ];
                map.value.fitBounds(bounds);
            }
        });

        /*
        onBeforeUnmount(() => {
            if (map.value) {
                map.value.off('draw.create', onDrawCreated);
                map.value.remove();
            }
        });
        */
        watch(() => props.currentBbox, (newBbox) => {
            updateBboxLayer(newBbox);
        }, { deep: true, immediate: true });

        watch(() => props.redrawBbox, (newValue) => {
            if (newValue) {
                updateBboxLayer(props.currentBbox);
            }
        });
        return { map, mapContainerId };
    }
};
</script>

<style>
.mapboxgl-canvas {
  border-radius: 20px;
}
</style>